import React, { useMemo, useRef, useState } from "react";
import * as axios from "axios";
import { Alert, Badge, Breadcrumbs, PageHeader, Textarea, TextInput, TileGroup, User01, Users01 } from "@opendate-app/setlist";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import SettingsSectionNav from "../../shared/SettingsSectionNav";
import { buildFormData, changedFields, MAX_LOGINS, MIN_LOGINS, sectionOf, slugify, validate, validLogins, visibleBenefits } from "./membershipFormLogic";
import { changeStatus } from "./membershipProductActions";
import { FormSection } from "./formControls";
import MembershipImageField from "./MembershipImageField";
import PricingSection from "./PricingSection";
import BenefitsSection from "./BenefitsSection";
import SellingSection from "./SellingSection";
import FanPreview from "./FanPreview";
import GuestSettings from "./GuestSettings";
import MembershipTeamRules from "./MembershipTeamRules";
import SaveBar from "./SaveBar";
import MembershipHeaderActions from "./MembershipHeaderActions";
import PreviewModal from "./PreviewModal";
import "./membershipProductEditor.css";

const STATUS_BADGES = {
  active: { color: "success", label: "Published" },
  draft: { color: "warning", label: "Draft" },
  archived: { color: "gray", label: "Archived" },
};

const MEMBERSHIP_TYPES = [
  { value: "individual", label: "Individual", icon: User01, supportingText: "One login, one member. Benefits tied to a single account." },
  {
    value: "household",
    label: "Household",
    icon: Users01,
    supportingText: "One purchase, shared across several logins. Everyone gets the full benefits.",
  },
];

const SECTION_IDS = {
  details: "membership-section-details",
  pricing: "membership-section-pricing",
  benefits: "membership-section-benefits",
  selling: "membership-section-selling",
};

const hasSectionError = (errors, section) => Object.keys(errors).some((field) => sectionOf(field) === section);
const fileNameFrom = (url) => (url ? decodeURIComponent(url.split("?")[0].split("/").pop()) : null);
const cadencesOf = (product, isEditing) => ({
  annual: !isEditing || !!(product.default_price && product.default_price.amount_cents),
  monthly: isEditing && !!(product.default_monthly_price && product.default_monthly_price.amount_cents),
});

const MembershipProductForm = () => {
  const {
    csrfToken,
    team,
    membershipProduct: product,
    originalMembershipProduct: original,
    updateMembershipProduct,
    updateDefaultMonthlyPrice,
    replaceMembershipProduct,
    discardChanges,
    activeMemberCount,
    householdSizes,
    isEditing,
    isSaving,
    setIsSaving,
    errors: serverErrors,
    setErrors,
  } = useMembershipProductNewContext();

  const loginsNow = product.household ? product.household_logins : 1;
  const overfullHouseholds =
    original?.household && (validLogins(loginsNow) || loginsNow === 1) ? householdSizes.filter((size) => size > loginsNow).length : 0;
  const [enabled, setEnabledState] = useState(() => cadencesOf(product, isEditing));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(product.image_url || null);
  const [uploadPct, setUploadPct] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [slugTouched, setSlugTouched] = useState(isEditing || !!product.slug);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishInFlight = useRef(false);
  const navigating = useRef(false);

  const archived = product.status === "archived";
  const cadenceOpts = { annualEnabled: enabled.annual, monthlyEnabled: enabled.monthly };
  const errors = useMemo(() => validate(product, { ...cadenceOpts, isEditing }), [product, enabled, isEditing]);
  const shownErrors = showErrors || isEditing ? errors : {};
  const changeCount = isEditing ? changedFields(original, product, { ...cadenceOpts, imageFile }).length : 0;

  const setEnabled = (cadence, on) => {
    setEnabledState((prev) => ({ ...prev, [cadence]: on }));
    if (cadence === "monthly" && on && !product.default_monthly_price) updateDefaultMonthlyPrice("amount_cents", 0);
  };

  const jumpTo = (section) => {
    const el = document.getElementById(SECTION_IDS[section]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const firstErrorSection = () => {
    const field = Object.keys(errors)[0];
    return field ? sectionOf(field) : null;
  };

  const setName = (name) =>
    updateMembershipProduct(isEditing || slugTouched ? { name } : { name, slug: slugify(name) });

  const discard = () => {
    discardChanges();
    setEnabledState(cadencesOf(original, isEditing));
    setImageFile(null);
    setImagePreviewUrl(original.image_url || null);
    setErrors([]);
  };

  const save = async () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      jumpTo(firstErrorSection());
      return null;
    }
    setIsSaving(true);
    setErrors([]);
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const config = {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (imageFile && event.total) setUploadPct(Math.round((event.loaded * 100) / event.total));
        },
      };
      const formData = buildFormData(product, { ...cadenceOpts, imageFile });
      const response = isEditing
        ? await axios.put(`/teams/${team.id}/membership_products/${product.id}`, formData, config)
        : await axios.post(`/teams/${team.id}/membership_products`, formData, config);

      toastSuccess(isEditing ? "Membership updated successfully" : "Membership created successfully");
      setImageFile(null);
      if (!isEditing) {
        navigating.current = true;
        window.location.href = `/teams/${team.id}/membership_products/${response.data.id}/edit`;
        return response.data;
      }
      replaceMembershipProduct(response.data);
      setImagePreviewUrl(response.data.image_url || null);
      setShowErrors(false);
      return response.data;
    } catch (error) {
      const messages = (error.response && error.response.data && error.response.data.errors) || ["An error occurred while saving"];
      setErrors(messages);
      toastError(messages[0]);
      return null;
    } finally {
      setUploadPct(null);
      if (!navigating.current) setIsSaving(false);
    }
  };

  const publish = async () => {
    if (publishInFlight.current) return;
    publishInFlight.current = true;
    setPublishing(true);
    try {
      const saved = changeCount ? await save() : product;
      if (!saved) return;
      const updated = await changeStatus({ teamId: team.id, product: saved, csrfToken, action: "publish" });
      if (updated) replaceMembershipProduct(updated);
    } finally {
      publishInFlight.current = false;
      setPublishing(false);
    }
  };

  const sections = [
    { id: SECTION_IDS.details, label: "Details", hasError: hasSectionError(shownErrors, "details") },
    { id: SECTION_IDS.pricing, label: "Pricing", hasError: hasSectionError(shownErrors, "pricing") },
    {
      id: SECTION_IDS.benefits,
      label: "Benefits",
      count: visibleBenefits(product).length,
      hasError: hasSectionError(shownErrors, "benefits"),
    },
    { id: SECTION_IDS.selling, label: "Where it's sold", hasError: hasSectionError(shownErrors, "selling") },
  ];

  const badge = STATUS_BADGES[product.status] || STATUS_BADGES.draft;
  const crumbs = [
    { label: "Settings", href: `/teams/${team.id}/settings` },
    { label: "Memberships", href: `/teams/${team.id}/membership_products` },
    ...(isEditing
      ? [{ label: original.name, href: `/teams/${team.id}/membership_products/${product.id}` }, { label: "Edit" }]
      : [{ label: "New" }]),
  ];

  return (
    <div className="membership-editor">
      <PageHeader
        breadcrumbs={<Breadcrumbs items={crumbs} />}
        title={
          <span className="membership-editor-title">
            {isEditing ? "Edit Membership" : "Create Membership"}
            <Badge color={badge.color} dot size="sm">
              {badge.label}
            </Badge>
            {isEditing && product.active_members_segment_id && activeMemberCount > 0 ? (
              <a
                className="membership-members-link"
                href={`/teams/${team.id}/segments/${product.active_members_segment_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Users01 width={16} height={16} aria-hidden="true" />
                View members ({activeMemberCount})
              </a>
            ) : null}
          </span>
        }
        actions={
          isEditing ? (
            <MembershipHeaderActions
              product={product}
              teamId={team.id}
              csrfToken={csrfToken}
              hasChanges={changeCount > 0}
              busy={isSaving || publishing}
              onPublish={publish}
              onProductUpdate={replaceMembershipProduct}
            />
          ) : null
        }
      />

      {serverErrors && serverErrors.length ? (
        <Alert color="error" title="The membership wasn't saved" description={serverErrors.join(" ")} />
      ) : null}

      <div className="membership-editor-layout">
        <div className="membership-editor-nav">
          <SettingsSectionNav sections={sections} label="Membership form sections" />
        </div>

        <fieldset className="membership-editor-main" disabled={archived}>
          <FormSection id={SECTION_IDS.details} title="Details" description="What fans see on the membership page.">
            <div className="membership-grid-2">
              <TextInput
                id="membership-name"
                label="Membership name"
                required
                placeholder="e.g. Patron Circle"
                value={product.name || ""}
                error={shownErrors.name}
                hint={shownErrors.name ? undefined : "Must be unique on your team."}
                onChange={(e) => setName(e.target.value)}
              />
              <MembershipImageField
                imageUrl={imagePreviewUrl}
                fileName={imageFile ? imageFile.name : fileNameFrom(product.image_url)}
                uploadProgress={uploadPct}
                disabled={isSaving || archived}
                onImage={(file) => {
                  setImageFile(file);
                  updateMembershipProduct({ _image_changed: true, _delete_image: false });
                  const reader = new FileReader();
                  reader.onloadend = () => setImagePreviewUrl(reader.result);
                  reader.readAsDataURL(file);
                }}
                onRemove={() => {
                  setImageFile(null);
                  setImagePreviewUrl(null);
                  updateMembershipProduct({ _image_changed: false, _delete_image: isEditing && !!product.image_url });
                }}
              />
            </div>
            <Textarea
              id="membership-description"
              label="Description"
              required
              rows={4}
              placeholder="Describe what this membership includes and why fans should join."
              value={product.description || ""}
              error={shownErrors.description}
              onChange={(e) => updateMembershipProduct({ description: e.target.value })}
            />
            <fieldset className="membership-type">
              <legend className="membership-label membership-type-legend">
                Membership type{" "}
                <Badge size="sm" color="brand">
                  New
                </Badge>
              </legend>
              <TileGroup
                type="radio"
                columns={2}
                name="membership-type"
                value={product.household ? "household" : "individual"}
                items={MEMBERSHIP_TYPES}
                onChange={(type) =>
                  updateMembershipProduct(
                    type === "household" || validLogins(product.household_logins)
                      ? { household: type === "household" }
                      : { household: false, household_logins: MIN_LOGINS }
                  )
                }
              />
              {product.household ? (
                <TextInput
                  id="membership-household-logins"
                  type="number"
                  min={MIN_LOGINS}
                  max={MAX_LOGINS}
                  step={1}
                  label="Logins included"
                  value={product.household_logins ?? ""}
                  error={shownErrors.household_logins}
                  hint={
                    shownErrors.household_logins
                      ? undefined
                      : "Adults with their own login. The buyer is the first member and invites the rest after purchase; venue staff can also add members from a contact’s Households tab."
                  }
                  onChange={(e) => updateMembershipProduct({ household_logins: e.target.value === "" ? null : Number(e.target.value) })}
                />
              ) : null}
              {product.household && team.can_access_membership_guests ? <GuestSettings errors={shownErrors} /> : null}
              {overfullHouseholds > 0 ? (
                <Alert
                  color="warning"
                  title={`${overfullHouseholds === 1 ? "1 household has" : `${overfullHouseholds} households have`} more people than this`}
                  description="When you save, their newest members lose access and we email them. If you add logins back later, they get access back in the order they joined."
                />
              ) : null}
            </fieldset>
          </FormSection>

          <FormSection
            id={SECTION_IDS.pricing}
            title="Pricing"
            description="Offer annual, monthly, or both. The ticketing price is what fans pay when they add it to a ticket order."
          >
            <PricingSection enabled={enabled} setEnabled={setEnabled} errors={shownErrors} />
          </FormSection>

          <FormSection
            id={SECTION_IDS.benefits}
            title="Benefits"
            description="Shown on checkout, the landing page, and event pages, in this order. Drag to reorder."
          >
            <BenefitsSection errors={shownErrors} disabled={archived} />
          </FormSection>

          <FormSection id={SECTION_IDS.selling} title="Where it's sold">
            <SellingSection errors={shownErrors} disabled={archived} onSlugEdited={() => setSlugTouched(true)} />
          </FormSection>
        </fieldset>

        <FanPreview
          teamName={team.name}
          product={product}
          imageUrl={imagePreviewUrl}
          enabled={enabled}
          onPreviewWidget={isEditing && !archived ? () => setPreviewOpen(true) : null}
        >
          <MembershipTeamRules disabled={archived} />
        </FanPreview>
      </div>

      <SaveBar
        archived={archived}
        isEditing={isEditing}
        isSaving={isSaving}
        changeCount={changeCount}
        errorCount={Object.keys(errors).length}
        showErrors={showErrors}
        onShowErrors={() => jumpTo(firstErrorSection())}
        onCancel={() => {
          window.location.href = `/teams/${team.id}/membership_products`;
        }}
        onDiscard={discard}
        onSave={save}
      />

      {previewOpen ? <PreviewModal teamId={team.id} membershipProduct={original} onClose={() => setPreviewOpen(false)} /> : null}
    </div>
  );
};

MembershipProductForm.propTypes = {};

export default MembershipProductForm;
